import { useMemo, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Button, Card, CardBody } from '@omnitok/ui';
import type { BadgeProps } from '@omnitok/ui';
import { Code2, Download, FileCode2, RefreshCw, Send } from 'lucide-react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { ConverterError, createStaticModelsService, validateSource } from '@/features/static-models/service';
import type { ConversionStatus, DownloadKind } from '@/features/static-models/types';
import '@/features/static-models/styles.css';

const labels: Record<ConversionStatus, string> = { queued: 'En cola', processing: 'Procesando', completed: 'Disponible', failed: 'Error' };
const variants: Record<ConversionStatus, BadgeProps['variant']> = { queued: 'neutral', processing: 'primary', completed: 'success', failed: 'error' };
const configuredUrl = (import.meta.env.VITE_STATICIZER_API_URL ?? '').trim();
const endpoint = configuredUrl || (import.meta.env.DEV ? 'http://127.0.0.1:8000' : '');
const date = (value: string) => new Date(value).toLocaleString('es-CL', { dateStyle: 'medium', timeStyle: 'short' });
const message = (cause: unknown) => cause instanceof Error ? cause.message : 'Ocurrió un error. Vuelve a intentarlo.';

export default function StaticModelsPage() {
  const { user, token } = useAuth();
  const client = useQueryClient();
  const service = useMemo(() => createStaticModelsService(endpoint, token ?? ''), [token]);
  const [url, setUrl] = useState('');
  const [cursor, setCursor] = useState<string | null>(null);
  const [previous, setPrevious] = useState<(string | null)[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [downloading, setDownloading] = useState<string | null>(null);
  const pendingRequest = useRef<{ url: string; key: string } | null>(null);
  const sending = useRef(false);
  const scope = ['static-models', user?.id, endpoint];
  const history = useQuery({
    queryKey: [...scope, cursor],
    queryFn: ({ signal }) => service.list(cursor, signal),
    enabled: !!token && !!endpoint,
    staleTime: 0,
    retry: (count, cause) => !(cause instanceof ConverterError && cause.status === 401) && count < 2,
    refetchInterval: query => {
      if (query.state.error instanceof ConverterError && query.state.error.status === 401) return false;
      if (query.state.error) return Math.min(5000 * 2 ** query.state.fetchFailureCount, 60000);
      return query.state.data?.items.some(row => ['queued', 'processing'].includes(row.status)) ? 5000 : false;
    },
  });
  const rows = history.data?.items ?? [];
  const pending = rows.filter(row => ['queued', 'processing'].includes(row.status)).length;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (sending.current) return;
    setError(''); setNotice('');
    let source: string;
    try { source = validateSource(url); } catch (cause) { setError(message(cause)); return; }
    if (!pendingRequest.current || pendingRequest.current.url !== source) {
      pendingRequest.current = { url: source, key: crypto.randomUUID() };
    }
    sending.current = true; setSubmitting(true);
    try {
      const accepted = await service.submit(source, pendingRequest.current.key);
      pendingRequest.current = null; setUrl(''); setCursor(null); setPrevious([]);
      setNotice(`Solicitud registrada. ${accepted.status === 'completed' ? 'Los archivos están disponibles.' : 'Puedes seguir su estado en el listado.'}`);
      await client.invalidateQueries({ queryKey: scope });
    } catch (cause) {
      setError(message(cause)); // Mantener la clave al reintentar una respuesta perdida.
    } finally { sending.current = false; setSubmitting(false); }
  }

  async function download(jobId: string, kind: DownloadKind) {
    setDownloading(jobId + kind); setError('');
    try {
      const href = await service.download(jobId, kind);
      const link = document.createElement('a');
      link.href = href; link.download = ''; link.rel = 'noreferrer';
      document.body.appendChild(link); link.click(); link.remove();
    } catch (cause) { setError(message(cause)); }
    finally { setDownloading(null); }
  }

  return <section className="static-models-page">
    <header className="static-models-header">
      <span className="static-models-eyebrow"><Code2 size={16} /> HERRAMIENTAS DE CONTENIDO</span>
      <h1>Modelos estáticos</h1>
      <p>Convierte una ficha Indexado en HTML y descarga la versión que necesitas.</p>
    </header>
    {!endpoint && <Alert variant="warning">El conversor todavía no está configurado para este entorno.</Alert>}
    <Card noPadding><CardBody>
      <form className="static-models-form" onSubmit={event => void submit(event)}>
        <div><h2>Nueva conversión</h2><p>Introduce la URL completa de una ficha Indexado V5.</p></div>
        <label htmlFor="static-model-source">URL de la ficha</label>
        <div className="static-models-input-row">
          <input id="static-model-source" type="url" required maxLength={2048} value={url} disabled={submitting || !endpoint}
            placeholder="https://front.indexado.production.alquimio.cloud/spec?modelId=…&lang=esCL&version=V5"
            aria-describedby="static-model-source-help" onChange={event => setUrl(event.target.value)} />
          <Button type="submit" variant="accent" disabled={!endpoint || !token || submitting || !url.trim()} loading={submitting} leftIcon={<Send size={16} />}>
            {submitting ? 'Enviando…' : 'Solicitar conversión'}
          </Button>
        </div>
        <small id="static-model-source-help">La conversión continúa aunque cierres esta página. Encontrarás tus solicitudes en el listado.</small>
      </form>
    </CardBody></Card>
    {error && <Alert variant="error">{error}</Alert>}
    {notice && <div role="status"><Alert variant="success">{notice}</Alert></div>}
    <Card noPadding>
      <div className="static-models-list-header">
        <div><h2>Mis conversiones</h2><p>{pending ? `${pending} solicitud${pending === 1 ? '' : 'es'} en curso en esta página · actualización automática` : 'Descarga los archivos de tus solicitudes completadas.'}</p></div>
        <Button variant="outline" size="sm" disabled={!endpoint || history.isFetching} leftIcon={<RefreshCw size={16} />} onClick={() => void history.refetch()}>
          {history.isFetching ? 'Actualizando…' : 'Actualizar'}
        </Button>
      </div>
      {history.error && <div className="static-models-feedback"><Alert variant="error">{message(history.error)} El listado puede estar desactualizado.</Alert></div>}
      {history.isLoading && <p className="static-models-empty" role="status">Cargando conversiones…</p>}
      {!history.isLoading && !history.error && !rows.length && <div className="static-models-empty"><FileCode2 size={32} /><h3>Aún no hay conversiones</h3><p>Envía la URL de una ficha para generar tus primeros archivos.</p></div>}
      {!!rows.length && <div className="static-models-table-scroll"><table className="static-models-table">
        <caption className="static-models-sr-only">Solicitudes de conversión y archivos disponibles</caption>
        <thead><tr><th scope="col">Modelo / solicitud</th><th scope="col">Creación</th><th scope="col">Estado</th><th scope="col">Archivos</th></tr></thead>
        <tbody>{rows.map(row => {
          const expired = Date.parse(row.expires_at) <= Date.now();
          const available = row.status === 'completed' && !expired;
          return <tr key={row.job_id}>
            <td><strong className="static-models-model-id">{row.model_id}</strong><span className="static-models-meta">{row.lang} · {row.profile === 'current' ? 'Estándar' : row.profile}</span><span className="static-models-job-id" title={row.job_id}>Solicitud {row.job_id.slice(0, 8)}</span></td>
            <td><time dateTime={row.created_at}>{date(row.created_at)}</time><span className="static-models-meta">Expira: {date(row.expires_at)}</span></td>
            <td><Badge size="sm" pill variant={expired && row.status === 'completed' ? 'neutral' : variants[row.status]}>{expired && row.status === 'completed' ? 'Expirado' : labels[row.status]}</Badge>
              {row.error && <p className="static-models-row-error">{row.error}</p>}
              {!!row.warnings.length && <details className="static-models-warnings"><summary>{row.warnings.length} advertencia{row.warnings.length === 1 ? '' : 's'}</summary><ul>{row.warnings.map((warning, index) => <li key={index}>{warning}</li>)}</ul></details>}
            </td>
            <td><div className="static-models-downloads">
              <Button size="sm" variant="outline" leftIcon={<Download size={15} />} disabled={!available || downloading !== null} loading={downloading === row.job_id + 'embed'} onClick={() => void download(row.job_id, 'embed')}>Retail embebido</Button>
              <Button size="sm" variant="outline" leftIcon={<Download size={15} />} disabled={!available || downloading !== null} loading={downloading === row.job_id + 'standalone'} onClick={() => void download(row.job_id, 'standalone')}>HTML estático</Button>
            </div>{!available && <small className="static-models-meta">{expired ? 'Archivos expirados' : row.status === 'failed' ? 'Conversión fallida' : 'Disponibles al completar'}</small>}</td>
          </tr>;
        })}</tbody>
      </table></div>}
      <div className="static-models-pagination">
        <span>Página {previous.length + 1}</span>
        <div><Button size="sm" variant="outline" disabled={!previous.length || history.isFetching} onClick={() => { setCursor(previous[previous.length - 1]); setPrevious(previous.slice(0, -1)); }}>Anterior</Button>
          <Button size="sm" variant="outline" disabled={!history.data?.next_cursor || history.isFetching} onClick={() => { setPrevious([...previous, cursor]); setCursor(history.data?.next_cursor ?? null); }}>Siguiente</Button></div>
      </div>
    </Card>
  </section>;
}
